import { FC } from 'react';

import { Link } from '@dbt-labs/sourdough';

import { PublicModelEdge } from '@/hooks/discovery/relatedProjects';
import { useLinkToExplorer } from '@/hooks/links';
import { useTargetEnvironmentType } from '@/hooks/useTargetEnvironmentType';
import { safeParseUniqueId } from '@/typings/parseUniqueId';

export interface ProjectPanelUpstreamRelationshipLinksProps {
  links: PublicModelEdge[];
  accountId: number;
  header: string;
  emptyResultsText: string;
  testId?: string;
}

export const ProjectPanelUpstreamRelationshipLinks: FC<
  ProjectPanelUpstreamRelationshipLinksProps
> = ({
  accountId,
  links,
  header,
  emptyResultsText,
  testId = 'project-panel-upstream-relationships',
}) => {
  const getTargetEnvironmentType = useTargetEnvironmentType();
  const modelDetailsLink = useLinkToExplorer('resourceDetails');

  const hasLinks = links.length > 0;
  return (
    <div>
      {hasLinks && (
        <>
          <div className="mt-4 text-xs font-medium text-fgAlt">{header}</div>
          <div className="mt-4">
            <ul className="space-y-2">
              {links.map((publicModel, index) => {
                const parsedUniqueId = safeParseUniqueId(publicModel.uniqueId).ok;
                const modelText = parsedUniqueId
                  ? `${parsedUniqueId.packageName}.${parsedUniqueId.remainder}`
                  : publicModel.uniqueId;
                const hasLinkData =
                  !!publicModel.projectId && !!publicModel.environmentId;
                return (
                  <li
                    key={publicModel.uniqueId}
                    className={`-mx-2 px-2 py-1 text-sm ${index % 2 === 1 ? 'bg-bgBackground' : ''}`}
                    data-testid={`${testId}-${index}`}
                  >
                    {hasLinkData ? (
                      <Link
                        isInternal
                        to={modelDetailsLink({
                          dbtUniqueId: publicModel.uniqueId,
                          accountId,
                          projectId: publicModel.projectId!,
                          environmentType: getTargetEnvironmentType(
                            publicModel.environmentId!,
                          ),
                        })}
                        className="text-sm"
                      >
                        {modelText}
                      </Link>
                    ) : (
                      modelText
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}
      {!hasLinks && (
        <div className="mt-4 italic text-fgDecorative">{emptyResultsText}</div>
      )}
    </div>
  );
};
